import type { AxiosError } from "axios";
import { apiClient } from "shared/api/client/orval";

function asAssetArrayFromResponse(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o["$values"])) return o["$values"];
    if (Array.isArray(o.items)) return o.items;
    if (Array.isArray(o.data)) return o.data;
  }
  return [];
}

function messageFromAxiosError(err: unknown): string {
  const ax = err as AxiosError<{
    error?: string;
    message?: string;
    title?: string;
    errors?: Record<string, string[] | string>;
  }>;
  const data = ax.response?.data;
  if (data && typeof data === "object") {
    if (typeof data.error === "string") return data.error;
    if (typeof data.title === "string") return data.title;
    if (typeof data.message === "string") return data.message;
    const nested = data.errors;
    if (nested && typeof nested === "object") {
      const vals = Object.values(nested).flat();
      const first = vals.find((v) => typeof v === "string") as string | undefined;
      if (first) return first;
    }
  }
  if (err instanceof Error) return err.message;
  return "Request failed.";
}

/** Curator-only queue (UnderReview titles). */
export async function fetchCuratorReviewQueue(): Promise<unknown[]> {
  const res = await apiClient.get("/api/Asset/get-queue-under-review");
  return asAssetArrayFromResponse(res.data);
}

export type CuratorAcceptResult = {
  body: unknown;
  usedEncodeFallback: boolean;
};

function isContractMismatch(err: unknown): boolean {
  const status = (err as AxiosError).response?.status;
  return status === 404 || status === 405 || status === 501;
}

/**
 * Accept contract, in order of preference:
 *   1. POST /api/Asset/review/accept        body { assetId }  (body-based, mirrors review/reject)
 *   2. POST /api/Asset/{id}/review/accept                      (path-based)
 *   3. POST /api/Asset/encode?assetId={id}                     (pre-U1.1 fallback)
 * A fallback is only attempted when the previous route does not exist
 * (404/405/501); real failures (401/403/409/5xx...) surface immediately.
 */
export async function curatorAcceptAsset(
  assetId: number
): Promise<CuratorAcceptResult> {
  try {
    const res = await apiClient.post(`/api/Asset/review/accept`, { assetId });
    return { body: res.data, usedEncodeFallback: false };
  } catch (first: unknown) {
    if (!isContractMismatch(first)) {
      throw new Error(messageFromAxiosError(first));
    }
  }

  try {
    const res = await apiClient.post(`/api/Asset/${assetId}/review/accept`);
    return { body: res.data, usedEncodeFallback: false };
  } catch (second: unknown) {
    if (!isContractMismatch(second)) {
      throw new Error(messageFromAxiosError(second));
    }
  }

  try {
    const res = await apiClient.post(`/api/Asset/encode`, undefined, {
      params: { assetId },
    });
    return { body: res.data, usedEncodeFallback: true };
  } catch (third: unknown) {
    throw new Error(messageFromAxiosError(third));
  }
}

/**
 * POST /api/Asset/review/reject — body-based contract.
 * Maps to AssetController.CuratorReject which binds
 * CuratorRejectRequestDto { AssetId, Reason } from the body.
 * Both fields are required by the backend.
 */
export async function curatorRejectAsset(
  assetId: number,
  reason: string
): Promise<unknown> {
  const trimmed = reason.trim();
  if (!trimmed) {
    throw new Error("Reason is required.");
  }
  try {
    const res = await apiClient.post(`/api/Asset/review/reject`, {
      assetId,
      reason: trimmed,
    });
    return res.data;
  } catch (err: unknown) {
    throw new Error(messageFromAxiosError(err));
  }
}
